import { AlertCircle, CircleDot, Scale, Unplug } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Modal } from '../../../shared/components/Modal';
import {
  connectBalanca,
  isBalancaSupported,
  type BalancaSession,
} from '../../../shared/lib/balanca';
import { formatBRL } from '../../../shared/lib/format';
import type { Produto } from '../../produtos/types';
import type { CartItem } from '../types';

interface Props {
  open: boolean;
  produto: Produto | null;
  onClose: () => void;
  onConfirm: (item: CartItem) => void;
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

type BalancaStatus = 'idle' | 'connecting' | 'reading' | 'error';

export function PesoModal({ open, produto, onClose, onConfirm }: Props) {
  const [peso, setPeso] = useState('');
  const [status, setStatus] = useState<BalancaStatus>('idle');
  const [oscilando, setOscilando] = useState(false);
  const sessionRef = useRef<BalancaSession | null>(null);

  useEffect(() => {
    if (open) {
      setPeso('');
      setStatus('idle');
      setOscilando(false);
    } else {
      void disconnect();
    }
    return () => {
      void disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function disconnect() {
    if (sessionRef.current) {
      try {
        await sessionRef.current.stop();
      } catch {
        // noop
      }
      sessionRef.current = null;
    }
  }

  async function handleConnect() {
    if (!isBalancaSupported()) {
      toast.error('Este navegador nao suporta Web Serial. Use Chrome ou Edge.');
      return;
    }
    setStatus('connecting');
    try {
      const session = await connectBalanca({
        onReading: (r) => {
          setStatus('reading');
          setOscilando(r.oscilando);
          if (!r.oscilando) {
            setPeso(r.kg.toFixed(3));
          }
        },
        onError: (err) => {
          setStatus('error');
          toast.error(`Balanca: ${err.message}`);
        },
      });
      sessionRef.current = session;
      toast.success('Balanca conectada');
    } catch (err) {
      setStatus('error');
      const msg = err instanceof Error ? err.message : String(err);
      if (/NotFoundError|No port selected/i.test(msg)) {
        setStatus('idle');
        return;
      }
      toast.error(`Nao foi possivel conectar: ${msg}`);
    }
  }

  if (!produto) return null;
  const precoKg = Number(produto.preco_por_kg ?? 0);
  const pesoNum = Number(peso) || 0;
  const subtotal = precoKg * pesoNum;

  const confirm = () => {
    if (pesoNum <= 0) return;
    onConfirm({
      uid: uid(),
      produto,
      tamanho_id: null,
      tamanho_nome: null,
      quantidade: Number(pesoNum.toFixed(3)),
      preco_base: precoKg,
      complementos: [],
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={produto.nome}
      description={`Preco: ${formatBRL(precoKg)}/kg`}
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            disabled={pesoNum <= 0 || oscilando}
            onClick={confirm}
          >
            Adicionar {formatBRL(subtotal)}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-900">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            <div>
              <p className="font-medium">
                {status === 'reading'
                  ? oscilando
                    ? 'Peso oscilando…'
                    : 'Lendo balanca'
                  : status === 'connecting'
                    ? 'Conectando…'
                    : 'Balanca desconectada'}
              </p>
              <p className="text-xs text-brand-800/80">
                {status === 'reading'
                  ? 'Aguarde estabilizar para confirmar'
                  : 'Conecte a balanca Toledo/Filizola via USB/Serial'}
              </p>
            </div>
          </div>
          {status === 'reading' ? (
            <button
              type="button"
              className="btn bg-white text-brand-700 hover:bg-slate-50"
              onClick={() => void disconnect().then(() => setStatus('idle'))}
            >
              <Unplug className="h-4 w-4" />
              Desconectar
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={status === 'connecting'}
              onClick={() => void handleConnect()}
            >
              <CircleDot className="h-4 w-4" />
              Conectar
            </button>
          )}
        </div>

        {!isBalancaSupported() && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Este navegador nao suporta Web Serial. Use Chrome/Edge ou informe o peso
              manualmente abaixo.
            </p>
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Peso (kg) {status === 'reading' && !oscilando && '· recebido da balanca'}
          </label>
          <input
            type="number"
            step="0.001"
            min={0}
            className={`input text-2xl font-semibold ${oscilando ? 'text-amber-600' : ''}`}
            value={peso}
            onChange={(e) => setPeso(e.target.value)}
            autoFocus
            placeholder="0.000"
          />
        </div>

        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Subtotal</span>
            <span className="font-bold text-brand-700">{formatBRL(subtotal)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
